import { Head, router } from "@inertiajs/react"
import { AppLayout } from "@/layouts/app-layout"
import { PageHeader } from "@/hooks/use-page-header"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Separator } from "@/components/ui/separator"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { UserDisplay } from "@/components/user-display"
import { RoleBadge } from "@/components/role-badge"
import { StatusIndicator } from "@/components/status-indicator"
import { Badge } from "@/components/ui/badge"
import { TeamCard } from "@/components/team-card"
import { TeamMembersDialog } from "@/components/team-members-dialog"
import { UserFormDialog } from "@/components/user-form-dialog"
import { TeamFormDialog } from "@/components/team-form-dialog"
import { OrganisationFormDialog } from "@/components/organisation-form-dialog"
import { PipeStatusManager } from "@/components/pipe-status-manager"
import { CourrierTemplateFormDialog } from "@/components/courrier-template-form-dialog"
import { Building2, Pencil, Plus, Search, Trash2, Users, UsersRound, GitBranch, FileText } from "lucide-react"
import { useState, useRef, useEffect, useMemo } from "react"
import type { Organisation, UserSummary, PipeStatusRecord, CourrierTemplate } from "@/types"
import type { TeamWithMembers } from "@/components/team-card"

type Props = {
  organisation: Organisation
  teams: TeamWithMembers[]
  users: UserSummary[]
  organisations: { id: number | string; name: string }[]
  all_teams: { id: number | string; name: string; organisation_id: number | string }[]
  can_edit_organisation: boolean
  can_create_teams: boolean
  can_manage_teams: boolean
  can_manage_users: boolean
  current_user_id: string
  pipe_statuses: PipeStatusRecord[]
  has_custom_pipe_statuses: boolean
  can_manage_pipe_statuses: boolean
  courrier_templates: CourrierTemplate[]
  can_manage_courrier_templates: boolean
  departments: { code: string; name: string }[]
  commune_names: { code: string; name: string; department_code: string }[]
}

const typeLabels: Record<string, string> = {
  promotor: "Promoteur",
  reseller: "Revendeur",
  agency: "Agence",
}

function MyOrganisationIndex({
  organisation,
  teams,
  users,
  organisations,
  all_teams,
  can_edit_organisation,
  can_create_teams,
  can_manage_teams,
  can_manage_users,
  current_user_id,
  pipe_statuses,
  has_custom_pipe_statuses,
  can_manage_pipe_statuses,
  courrier_templates,
  can_manage_courrier_templates,
  departments,
  commune_names,
}: Props) {
  const [search, setSearch] = useState("")
  const [activeTab, setActiveTab] = useState("teams")
  const searchRef = useRef<HTMLInputElement>(null)

  const [showUserForm, setShowUserForm] = useState(false)
  const [editingUser, setEditingUser] = useState<UserSummary | null>(null)
  const [showTeamForm, setShowTeamForm] = useState(false)
  const [editingTeam, setEditingTeam] = useState<TeamWithMembers | null>(null)
  const [showOrgForm, setShowOrgForm] = useState(false)

  const [deletingUser, setDeletingUser] = useState<UserSummary | null>(null)
  const [deletingTeam, setDeletingTeam] = useState<TeamWithMembers | null>(null)
  const [managingMembersTeam, setManagingMembersTeam] = useState<TeamWithMembers | null>(null)

  const [showTemplateForm, setShowTemplateForm] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<CourrierTemplate | null>(null)
  const [deletingTemplate, setDeletingTemplate] = useState<CourrierTemplate | null>(null)

  useEffect(() => {
    searchRef.current?.focus()
  }, [])

  const lowerSearch = search.toLowerCase()

  const filteredTeams = useMemo(
    () =>
      teams.filter(
        (t) =>
          t.name.toLowerCase().includes(lowerSearch) ||
          t.team_memberships?.some(
            (m) =>
              m.user.first_name.toLowerCase().includes(lowerSearch) ||
              m.user.last_name.toLowerCase().includes(lowerSearch),
          ),
      ),
    [teams, lowerSearch],
  )

  const filteredUsers = useMemo(
    () =>
      users.filter(
        (u) =>
          u.first_name.toLowerCase().includes(lowerSearch) ||
          u.last_name.toLowerCase().includes(lowerSearch) ||
          u.email_address.toLowerCase().includes(lowerSearch),
      ),
    [users, lowerSearch],
  )

  const handleDeleteUser = () => {
    if (!deletingUser) return
    router.delete(`/users/${deletingUser.id}`, {
      onSuccess: () => setDeletingUser(null),
    })
  }

  const handleDeleteTeam = () => {
    if (!deletingTeam) return
    router.delete(`/teams/${deletingTeam.id}`, {
      onSuccess: () => setDeletingTeam(null),
    })
  }

  return (
    <>
      <Head title="Mon organisation" />
      <PageHeader title="Mon organisation" />
      <div className="space-y-6">
        {/* Organisation header */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Building2 className="size-5 text-muted-foreground" />
              {organisation.name}
            </CardTitle>
            <CardDescription className="flex items-center gap-2">
              <Badge variant="secondary">{typeLabels[organisation.organisation_type]}</Badge>
              <StatusIndicator active={organisation.is_active} />
              {organisation.is_demo && <Badge variant="outline">Démo</Badge>}
            </CardDescription>
            {can_edit_organisation && (
              <CardAction>
                <Button variant="outline" size="sm" onClick={() => setShowOrgForm(true)}>
                  <Pencil className="mr-2 size-3.5" />
                  Modifier
                </Button>
              </CardAction>
            )}
          </CardHeader>
          <CardContent>
            <Separator className="mb-4" />
            <Tabs defaultValue="teams" onValueChange={(v) => setActiveTab(v)}>
              <div className="flex items-center justify-between gap-4">
                <TabsList variant="line">
                  <TabsTrigger value="teams" className="cursor-pointer">
                    Équipes ({teams.length})
                  </TabsTrigger>
                  <TabsTrigger value="users" className="cursor-pointer">
                    Utilisateurs ({users.length})
                  </TabsTrigger>
                  <TabsTrigger value="pipe_statuses" className="cursor-pointer">
                    Statuts pipeline
                  </TabsTrigger>
                  <TabsTrigger value="courrier_templates" className="cursor-pointer">
                    Modèles courrier ({courrier_templates?.length || 0})
                  </TabsTrigger>
                </TabsList>
                <div className="flex items-center gap-2">
                  {activeTab !== "pipe_statuses" && activeTab !== "courrier_templates" && (
                    <div className="relative max-w-xs">
                      <Search className="text-muted-foreground absolute left-3 top-1/2 size-4 -translate-y-1/2" />
                      <Input
                        ref={searchRef}
                        placeholder="Rechercher..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-9"
                      />
                    </div>
                  )}
                  {activeTab === "teams" && can_create_teams && (
                    <Button
                      size="sm"
                      onClick={() => {
                        setEditingTeam(null)
                        setShowTeamForm(true)
                      }}
                    >
                      <Plus className="size-4" />
                      Nouvelle équipe
                    </Button>
                  )}
                  {activeTab === "users" && can_manage_users && (
                    <Button
                      size="sm"
                      onClick={() => {
                        setEditingUser(null)
                        setShowUserForm(true)
                      }}
                    >
                      <Plus className="size-4" />
                      Nouvel utilisateur
                    </Button>
                  )}
                  {activeTab === "courrier_templates" && can_manage_courrier_templates && (
                    <Button
                      size="sm"
                      onClick={() => {
                        setEditingTemplate(null)
                        setShowTemplateForm(true)
                      }}
                    >
                      <Plus className="size-4" />
                      Nouveau modèle
                    </Button>
                  )}
                </div>
              </div>

              {/* Teams tab */}
              <TabsContent value="teams">
                <div className="space-y-4">
                  {filteredTeams.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                      <UsersRound className="size-10 mb-3 opacity-40" />
                      <p className="text-sm">Aucune équipe trouvée</p>
                    </div>
                  ) : (
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {filteredTeams.map((team) => {
                        const isManagerOfTeam = team.team_memberships?.some(
                          (m) => String(m.user.id) === String(current_user_id) && m.role === "manager",
                        )
                        return (
                        <TeamCard
                          key={team.id}
                          team={team}
                          canEdit={can_create_teams || !!isManagerOfTeam}
                          onEdit={(t) => {
                            setEditingTeam(t)
                            setShowTeamForm(true)
                          }}
                          onDelete={can_create_teams ? (t) => setDeletingTeam(t) : undefined}
                          onManageMembers={(t) => setManagingMembersTeam(t)}
                        />
                        )
                      })}
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* Users tab */}
              <TabsContent value="users">
                <div className="space-y-4">
                  {filteredUsers.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                      <Users className="size-10 mb-3 opacity-40" />
                      <p className="text-sm">Aucun utilisateur trouvé</p>
                    </div>
                  ) : (
                    <Card className="p-0">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Utilisateur</TableHead>
                            <TableHead>Rôle</TableHead>
                            <TableHead>Statut</TableHead>
                            {can_manage_users && <TableHead />}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredUsers.map((user) => {
                            const isSelf = String(user.id) === String(current_user_id)
                            return (
                              <TableRow key={user.id}>
                                <TableCell className="font-medium">
                                  <UserDisplay user={user} size="sm" showEmail />
                                  {user.is_super_admin && (
                                    <Badge variant="destructive" className="ml-2">
                                      Super Admin
                                    </Badge>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <RoleBadge role={user.role} />
                                </TableCell>
                                <TableCell>
                                  <StatusIndicator active={user.is_active} />
                                </TableCell>
                                {can_manage_users && (
                                  <TableCell className="text-right">
                                    <div className="flex items-center justify-end gap-1">
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Button
                                            variant="ghost"
                                            size="icon-sm"
                                            onClick={() => {
                                              setEditingUser(user)
                                              setShowUserForm(true)
                                            }}
                                          >
                                            <Pencil className="size-3.5" />
                                          </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>Modifier</TooltipContent>
                                      </Tooltip>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Button
                                            variant="ghost"
                                            size="icon-sm"
                                            disabled={isSelf}
                                            onClick={() => setDeletingUser(user)}
                                            className="text-destructive hover:text-destructive"
                                          >
                                            <Trash2 className="size-3.5" />
                                          </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          {isSelf ? "Vous ne pouvez pas désactiver votre propre compte" : "Désactiver"}
                                        </TooltipContent>
                                      </Tooltip>
                                    </div>
                                  </TableCell>
                                )}
                              </TableRow>
                            )
                          })}
                        </TableBody>
                      </Table>
                    </Card>
                  )}
                </div>
              </TabsContent>

              {/* Pipe statuses tab */}
              <TabsContent value="pipe_statuses">
                <PipeStatusManager
                  pipeStatuses={pipe_statuses}
                  hasCustomPipeStatuses={has_custom_pipe_statuses}
                  canManage={can_manage_pipe_statuses}
                />
              </TabsContent>

              {/* Courrier templates tab */}
              <TabsContent value="courrier_templates">
                <div className="space-y-4">
                  {(!courrier_templates || courrier_templates.length === 0) ? (
                    <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                      <FileText className="size-10 mb-3 opacity-40" />
                      <p className="text-sm">Aucun modèle de courrier configuré</p>
                    </div>
                  ) : (
                    <Card className="p-0">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Titre</TableHead>
                            <TableHead>Template PDFMonkey</TableHead>
                            <TableHead>Corps</TableHead>
                            {can_manage_courrier_templates && <TableHead />}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {courrier_templates.map((template) => (
                            <TableRow key={template.id}>
                              <TableCell className="font-medium">{template.title}</TableCell>
                              <TableCell>
                                <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                                  {template.pdfmonkey_template_id.slice(0, 12)}...
                                </code>
                              </TableCell>
                              <TableCell className="max-w-xs truncate text-muted-foreground text-sm">
                                {template.body ? template.body.slice(0, 60) + "..." : "—"}
                              </TableCell>
                              {can_manage_courrier_templates && (
                                <TableCell className="text-right">
                                  <div className="flex items-center justify-end gap-1">
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="icon-sm"
                                          onClick={() => {
                                            setEditingTemplate(template)
                                            setShowTemplateForm(true)
                                          }}
                                        >
                                          <Pencil className="size-3.5" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>Modifier</TooltipContent>
                                    </Tooltip>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="icon-sm"
                                          onClick={() => setDeletingTemplate(template)}
                                          className="text-destructive hover:text-destructive"
                                        >
                                          <Trash2 className="size-3.5" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>Supprimer</TooltipContent>
                                    </Tooltip>
                                  </div>
                                </TableCell>
                              )}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Card>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>

      {/* Modals */}
      <UserFormDialog
        open={showUserForm}
        onOpenChange={setShowUserForm}
        user={editingUser}
        organisations={organisations}
        teams={all_teams}
      />
      <TeamFormDialog
        open={showTeamForm}
        onOpenChange={setShowTeamForm}
        team={editingTeam}
        organisations={organisations}
        allTeams={all_teams}
      />
      <OrganisationFormDialog open={showOrgForm} onOpenChange={setShowOrgForm} organisation={organisation} departments={departments} communeNames={commune_names} />

      {/* Team members dialog */}
      {managingMembersTeam && (
        <TeamMembersDialog
          open={!!managingMembersTeam}
          onOpenChange={(open) => {
            if (!open) setManagingMembersTeam(null)
          }}
          team={managingMembersTeam}
          allUsers={users}
        />
      )}

      {/* Delete user confirmation */}
      <AlertDialog
        open={!!deletingUser}
        onOpenChange={(open) => {
          if (!open) setDeletingUser(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Désactiver l'utilisateur</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir désactiver {deletingUser?.first_name} {deletingUser?.last_name} ? L'utilisateur
              ne pourra plus se connecter mais ses données seront conservées.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteUser}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Désactiver
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Courrier template form */}
      <CourrierTemplateFormDialog
        open={showTemplateForm}
        onOpenChange={setShowTemplateForm}
        template={editingTemplate}
      />

      {/* Delete courrier template confirmation */}
      <AlertDialog
        open={!!deletingTemplate}
        onOpenChange={(open) => {
          if (!open) setDeletingTemplate(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer le modèle</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer le modèle « {deletingTemplate?.title} » ? Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deletingTemplate) {
                  router.delete(`/courrier_templates/${deletingTemplate.id}`, {
                    onSuccess: () => setDeletingTemplate(null),
                  })
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete team confirmation */}
      <AlertDialog
        open={!!deletingTeam}
        onOpenChange={(open) => {
          if (!open) setDeletingTeam(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer l'équipe</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer l'équipe « {deletingTeam?.name} » ? Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteTeam}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

MyOrganisationIndex.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>

export default MyOrganisationIndex
